import type { ChainedCommands, Editor, JSONContent, Range } from '@tiptap/core'
import { Extension } from '@tiptap/core'
import { Table } from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import type { Node as ProsemirrorNode } from '@tiptap/pm/model'
import { Plugin } from '@tiptap/pm/state'
import { CellSelection, findTable, selectedRect, selectionCell, TableMap, TableView } from '@tiptap/pm/tables'
import { findChildren } from '@tiptap/react'

const TABLE_CELL_ALIGNMENTS = new Set(['left', 'center', 'right', 'justify'])
const TABLE_ELEMENT_SELECTOR = 'table, thead, tbody, tfoot, tr, th, td'
const TABLE_CELL_NODE_NAMES = new Set(['tableCell', 'tableHeader'])
const TABLE_ROW_NODE_NAME = 'tableRow'

export type TableSelectionAxis = 'row' | 'column'
export type TableSelectionVariant = TableSelectionAxis | 'cellRange' | 'cell'
export type TableInsertDirection = 'before' | 'after'
export type TableAxisTarget = { kind: 'axis'; axis: TableSelectionAxis; index: number }
export type TableSelectionTarget = TableAxisTarget | { kind: 'cell'; element: HTMLElement }
export type TableActionType =
    | 'insertRowBefore'
    | 'insertRowAfter'
    | 'insertColumnBefore'
    | 'insertColumnAfter'
    | 'deleteRow'
    | 'deleteColumn'
    | 'deleteTable'
    | 'mergeCells'
    | 'splitCell'
    | 'toggleHeaderRow'
    | 'toggleHeaderColumn'
export type TableTextStyleAction =
    | { type: 'bold' }
    | { type: 'italic' }
    | { type: 'underline' }
    | { type: 'strike' }
    | { type: 'textColor'; value?: string | null }
    | { type: 'highlight'; value?: string | null }
    | { type: 'textAlign'; value?: 'left' | 'center' | 'right' | null }

export interface TableSelectionBookmark {
    anchorCell: number
    headCell: number
}

interface TableSelectionSnapshot {
    variant: TableSelectionVariant
    top: number
    bottom: number
    left: number
    right: number
    currentRow: number
    currentCol: number
}

export interface TableRectInfo {
    table: ProsemirrorNode
    tableStart: number
    map: TableMap
    left: number
    right: number
    top: number
    bottom: number
}

export interface TableCellRect extends TableRectInfo {
    cell: ProsemirrorNode
    cellPos: number
    row: number
    col: number
}

export interface TableSelectionInfo extends TableRectInfo {
    axis: TableSelectionAxis | 'cell'
    rowIndexes: number[]
    colIndexes: number[]
}

export interface TableSelectionShape {
    isSingleCell: boolean
    isCellRange: boolean
    isFullRowSpan: boolean
    isFullColumnSpan: boolean
}

export interface TableAnchorRects {
    tableRect: DOMRect
    columnRects: DOMRect[]
    rowRects: DOMRect[]
}

export interface TableSelectionOutlineSegment {
    top: number
    left: number
    width: number
    height: number
}

const COMMON_TABLE_HTML_ATTRIBUTES = {
    class: 'rich-text-table'
}

const COMMON_ROW_HTML_ATTRIBUTES = {
    class: 'rich-text-table-row'
}

const COMMON_HEADER_HTML_ATTRIBUTES = {
    class: 'rich-text-table-header'
}

const COMMON_CELL_HTML_ATTRIBUTES = {
    class: 'rich-text-table-cell'
}

class RichTextEditableTableView extends TableView {
    constructor(node: ProsemirrorNode, cellMinWidth: number) {
        super(node, cellMinWidth)
        this.table.classList.add('rich-text-table')
    }
}

export const ReadonlyTable = Table.configure({
    resizable: false,
    renderWrapper: true,
    HTMLAttributes: COMMON_TABLE_HTML_ATTRIBUTES
})

export const EditableTable = Table.configure({
    resizable: true,
    cellMinWidth: 80,
    View: RichTextEditableTableView,
    HTMLAttributes: COMMON_TABLE_HTML_ATTRIBUTES
})

export const ReadonlyTableRow = TableRow.configure({
    HTMLAttributes: COMMON_ROW_HTML_ATTRIBUTES
})

export const EditableTableRow = TableRow.configure({
    HTMLAttributes: COMMON_ROW_HTML_ATTRIBUTES
})

export const ReadonlyTableHeader = TableHeader.configure({
    HTMLAttributes: COMMON_HEADER_HTML_ATTRIBUTES
})

export const EditableTableHeader = TableHeader.configure({
    HTMLAttributes: COMMON_HEADER_HTML_ATTRIBUTES
})

export const ReadonlyTableCell = TableCell.configure({
    HTMLAttributes: COMMON_CELL_HTML_ATTRIBUTES
})

export const EditableTableCell = TableCell.configure({
    HTMLAttributes: COMMON_CELL_HTML_ATTRIBUTES
})

export function normalizeRichTextPastedHTML(html: string): string {
    if (typeof DOMParser === 'undefined') {
        return html
    }

    const doc = new DOMParser().parseFromString(html, 'text/html')
    const hasTable = !!doc.body.querySelector('table, tr, th, td')

    if (hasTable) {
        sanitizeTableMarkup(doc)
    }

    normalizeBreakTags(doc, { skipInsideTables: hasTable })

    return doc.body.innerHTML
}

function sanitizeTableMarkup(doc: Document) {
    doc.body.querySelectorAll('colgroup, col').forEach(node => {
        node.remove()
    })

    doc.body.querySelectorAll<HTMLElement>(TABLE_ELEMENT_SELECTOR).forEach(element => {
        const attrs = getAllowedTableAttributes(element)

        ;[...element.attributes].forEach(attr => {
            element.removeAttribute(attr.name)
        })

        Object.entries(attrs).forEach(([key, value]) => {
            element.setAttribute(key, value)
        })
    })
}

function getAllowedTableAttributes(element: HTMLElement): Record<string, string> {
    const tagName = element.tagName.toLowerCase()

    if (tagName === 'table') {
        return { class: 'rich-text-table' }
    }

    if (tagName === 'tr') {
        return { class: 'rich-text-table-row' }
    }

    if (tagName === 'th' || tagName === 'td') {
        const attrs: Record<string, string> = {
            class: tagName === 'th' ? 'rich-text-table-header' : 'rich-text-table-cell'
        }

        const colspan = element.getAttribute('colspan')
        const rowspan = element.getAttribute('rowspan')
        const colwidth = element.getAttribute('colwidth')
        const textAlign = getAllowedCellTextAlign(element)

        if (colspan) {
            attrs.colspan = colspan
        }
        if (rowspan) {
            attrs.rowspan = rowspan
        }
        if (colwidth) {
            attrs.colwidth = colwidth
        }
        if (textAlign) {
            attrs.style = `text-align: ${textAlign};`
        }

        return attrs
    }

    return {}
}

function getAllowedCellTextAlign(element: HTMLElement) {
    const rawValue = (element.style.textAlign || element.getAttribute('align') || '').trim().toLowerCase()
    return TABLE_CELL_ALIGNMENTS.has(rawValue) ? rawValue : null
}

function normalizeBreakTags(doc: Document, options?: { skipInsideTables?: boolean }) {
    const brs = [...doc.body.querySelectorAll('br')]

    brs.forEach(br => {
        if (options?.skipInsideTables && br.closest('table')) {
            return
        }

        const parent = br.parentElement
        if (!parent) {
            return
        }

        if (parent.tagName === 'P') {
            const newP = doc.createElement('p')
            while (br.nextSibling) {
                newP.appendChild(br.nextSibling)
            }
            if (!newP.hasChildNodes()) {
                newP.appendChild(doc.createElement('br'))
            }
            parent.after(newP)
            br.remove()
            return
        }

        if (parent === doc.body || parent.tagName === 'DIV') {
            const p = doc.createElement('p')
            p.appendChild(doc.createElement('br'))
            br.replaceWith(p)
        }
    })
}

export function getTableContentFromTSV(text: string): JSONContent | null {
    const normalized = text.replace(/\r\n?/g, '\n')
    const rows = normalized.split('\n')

    while (rows.length > 0 && rows[rows.length - 1] === '') {
        rows.pop()
    }

    if (rows.length < 2) {
        return null
    }

    const cells = rows.map(row => row.split('\t'))
    const columnCount = cells[0]?.length ?? 0

    if (columnCount < 2 || cells.some(row => row.length !== columnCount)) {
        return null
    }

    return {
        type: 'table',
        content: cells.map(row => ({
            type: 'tableRow',
            content: row.map(cell => ({
                type: 'tableCell',
                content: cell
                    ? [
                          {
                              type: 'paragraph',
                              content: [{ type: 'text', text: cell }]
                          }
                      ]
                    : [{ type: 'paragraph' }]
            }))
        }))
    }
}

export const TablePaste = Extension.create({
    name: 'tablePaste',

    addProseMirrorPlugins() {
        return [
            new Plugin({
                props: {
                    handlePaste: (_view, event) => {
                        const clipboardData = event.clipboardData

                        if (!clipboardData) {
                            return false
                        }

                        const hasFile = [...(clipboardData.items ?? [])].some(item => item.kind === 'file')
                        if (hasFile) {
                            return false
                        }

                        const html = clipboardData.getData('text/html')?.trim()
                        if (html) {
                            return false
                        }

                        const text = clipboardData.getData('text/plain')
                        const tableContent = getTableContentFromTSV(text)

                        if (!tableContent) {
                            return false
                        }

                        event.preventDefault()
                        return this.editor.commands.insertContent(tableContent)
                    }
                }
            })
        ]
    }
})

export function getTableSelectionInfo(editor: Editor): TableSelectionInfo | null {
    if (!isSelectionInTable(editor)) {
        return null
    }

    const rect = selectedRect(editor.state)
    const rowIndexes = getRangeIndexes(rect.top, rect.bottom)
    const colIndexes = getRangeIndexes(rect.left, rect.right)

    return {
        ...rect,
        axis: getSelectionAxis(editor, rect),
        rowIndexes,
        colIndexes
    }
}

export function getCurrentCellRect(editor: Editor): TableCellRect | null {
    if (!isSelectionInTable(editor)) {
        return null
    }

    const $cell = selectionCell(editor.state)
    const rect = selectedRect(editor.state)
    const cellPos = $cell.pos - rect.tableStart
    const cellRect = rect.map.findCell(cellPos)
    const cell = rect.table.nodeAt(cellPos)

    if (!cell) {
        return null
    }

    return {
        ...rect,
        cell,
        cellPos,
        row: cellRect.top,
        col: cellRect.left
    }
}

export function isSelectionInTable(editor: Editor): boolean {
    const selection = editor.state.selection
    const anchorInTable = findTable(selection.$anchor)
    const headInTable = findTable(selection.$head)

    if (anchorInTable && headInTable) {
        return anchorInTable.pos === headInTable.pos
    }

    return selection instanceof CellSelection
}

export function getSelectionAxis(editor: Editor, info?: TableRectInfo): TableSelectionAxis | 'cell' {
    const shape = getTableSelectionShapeFromRectInfo(info ?? selectedRect(editor.state))
    const variant = getTableSelectionVariantFromShape(shape)
    return variant === 'cellRange' ? 'cell' : variant
}

export function isRowSelection(editor: Editor): boolean {
    return getSelectionAxis(editor) === 'row'
}

export function isColumnSelection(editor: Editor): boolean {
    return getSelectionAxis(editor) === 'column'
}

export function isCellRangeSelection(editor: Editor): boolean {
    return getTableSelectionVariant(editor) === 'cellRange'
}

export function getTableSelectionVariantFromInfo(selection: TableSelectionInfo): TableSelectionVariant {
    return getTableSelectionVariantFromShape(getTableSelectionShape(selection))
}

export function getTableSelectionVariant(editor: Editor): TableSelectionVariant | null {
    const selection = getTableSelectionInfo(editor)
    if (!selection) {
        return null
    }

    return getTableSelectionVariantFromInfo(selection)
}

export function getTableSelectionShape(selection: TableSelectionInfo): TableSelectionShape {
    return getTableSelectionShapeFromRectInfo(selection)
}

export function getTableSelectionVariantFromShape(shape: TableSelectionShape): TableSelectionVariant {
    if (shape.isFullRowSpan) {
        return 'row'
    }
    if (shape.isFullColumnSpan) {
        return 'column'
    }
    if (shape.isCellRange) {
        return 'cellRange'
    }
    return 'cell'
}

function getTableSelectionShapeFromRectInfo(info: TableRectInfo): TableSelectionShape {
    const rowSpan = info.bottom - info.top
    const colSpan = info.right - info.left

    return {
        isSingleCell: rowSpan === 1 && colSpan === 1,
        isCellRange: rowSpan > 1 || colSpan > 1,
        isFullRowSpan: colSpan === info.map.width && rowSpan > 0,
        isFullColumnSpan: rowSpan === info.map.height && colSpan > 0
    }
}

export function selectTableRow(editor: Editor, rowIndex: number): boolean {
    const currentCell = getCurrentCellRect(editor)
    if (!currentCell) {
        return false
    }

    const row = clamp(rowIndex, 0, currentCell.map.height - 1)
    const left = currentCell.map.positionAt(row, 0, currentCell.table)
    const right = currentCell.map.positionAt(row, currentCell.map.width - 1, currentCell.table)

    return setTableSelection(editor, currentCell.tableStart + left, currentCell.tableStart + right)
}

export function selectTableColumn(editor: Editor, colIndex: number): boolean {
    const currentCell = getCurrentCellRect(editor)
    if (!currentCell) {
        return false
    }

    const col = clamp(colIndex, 0, currentCell.map.width - 1)
    const top = currentCell.map.positionAt(0, col, currentCell.table)
    const bottom = currentCell.map.positionAt(currentCell.map.height - 1, col, currentCell.table)

    return setTableSelection(editor, currentCell.tableStart + top, currentCell.tableStart + bottom)
}

export function selectTableAxis(editor: Editor, axis: TableSelectionAxis, index: number): boolean {
    return axis === 'row' ? selectTableRow(editor, index) : selectTableColumn(editor, index)
}

export function prepareTableSelection(editor: Editor, target: TableSelectionTarget): TableSelectionVariant | null {
    const prepared =
        target.kind === 'axis'
            ? selectTableAxis(editor, target.axis, target.index)
            : isTableCellElementInSelection(editor, target.element) || focusTableCellFromElement(editor, target.element)

    if (!prepared) {
        return null
    }

    return getTableSelectionVariant(editor)
}

function setTableSelection(editor: Editor, anchorCell: number, headCell: number) {
    try {
        const selection = CellSelection.create(editor.state.doc, anchorCell, headCell)
        const tr = editor.state.tr.setSelection(selection)
        editor.view.dispatch(tr)
        editor.view.focus()
        return true
    } catch {
        return false
    }
}

export function applyTableTextStyle(editor: Editor, action: TableTextStyleAction): boolean {
    const selection = getTableSelectionInfo(editor)
    if (!selection) {
        return false
    }

    const ranges = getSelectedCellContentRanges(selection)
    if (ranges.length === 0) {
        return false
    }

    const bookmark = getCurrentTableSelectionBookmark(editor) ?? getTableSelectionBookmarkFromInfo(selection)
    let chain = editor.chain().focus()

    for (const range of ranges) {
        chain = applyTextStyleToRange(chain, action, range)
    }

    return chain
        .command(({ tr }) => {
            tr.setSelection(CellSelection.create(tr.doc, bookmark.anchorCell, bookmark.headCell))
            return true
        })
        .run()
}

export function isTableTextStyleActive(editor: Editor, action: TableTextStyleAction): boolean {
    const selection = getTableSelectionInfo(editor)
    if (!selection) {
        return false
    }

    const selectedCells = getSelectedTableCells(selection)
    if (selectedCells.length === 0) {
        return false
    }

    switch (action.type) {
        case 'bold':
        case 'italic':
        case 'underline':
        case 'strike':
            return selectedCells.every(cell => isMarkActiveInCell(cell, action.type))
        case 'textAlign': {
            const alignValue = action.value
            return alignValue ? selectedCells.every(cell => isTextAlignActiveInCell(cell, alignValue)) : false
        }
        default:
            return false
    }
}

function applyTextStyleToRange(chain: ChainedCommands, action: TableTextStyleAction, range: Range) {
    const next = chain.setTextSelection(range)

    switch (action.type) {
        case 'bold':
            return next.toggleBold()
        case 'italic':
            return next.toggleItalic()
        case 'underline':
            return next.toggleUnderline()
        case 'strike':
            return next.toggleStrike()
        case 'textColor':
            return action.value ? next.setColor(action.value) : next.unsetColor()
        case 'highlight':
            return action.value ? next.setHighlight({ color: action.value }) : next.unsetHighlight()
        case 'textAlign':
            return action.value ? next.setTextAlign(action.value) : next.unsetTextAlign()
        default:
            return next
    }
}

export function insertTableRow(editor: Editor, direction: TableInsertDirection): boolean {
    return insertAndFocusTableCell(editor, 'row', direction)
}

export function insertTableColumn(editor: Editor, direction: TableInsertDirection): boolean {
    return insertAndFocusTableCell(editor, 'column', direction)
}

export function insertTableAt(editor: Editor, axis: TableSelectionAxis, index: number, direction: TableInsertDirection): boolean {
    return insertTableAtTarget(editor, { kind: 'axis', axis, index }, direction)
}

export function insertTableAtTarget(editor: Editor, target: TableAxisTarget, direction: TableInsertDirection): boolean {
    const prepared = prepareTableSelection(editor, target)
    if (!prepared) {
        return false
    }

    return insertAndFocusTableCell(editor, target.axis, direction, target.index)
}

export function deleteSelectedTableRow(editor: Editor): boolean {
    return runAndRestoreTableSelection(editor, () => editor.commands.deleteRow())
}

export function deleteSelectedTableColumn(editor: Editor): boolean {
    return runAndRestoreTableSelection(editor, () => editor.commands.deleteColumn())
}

export function deleteCurrentTable(editor: Editor): boolean {
    return editor.commands.deleteTable()
}

export function mergeSelectedTableCells(editor: Editor): boolean {
    return runAndRestoreTableSelection(editor, () => editor.commands.mergeCells())
}

export function splitSelectedTableCell(editor: Editor): boolean {
    return runAndRestoreTableSelection(editor, () => editor.commands.splitCell())
}

function hasTableExtension(editor: Editor): boolean {
    if (editor.isDestroyed || !editor.extensionManager) {
        return false
    }
    return editor.extensionManager.extensions.some(e => e.name === 'table')
}

export function canMergeSelectedTableCells(editor: Editor): boolean {
    if (!hasTableExtension(editor)) {
        return false
    }
    return editor.can().mergeCells()
}

export function canSplitSelectedTableCell(editor: Editor): boolean {
    if (!hasTableExtension(editor)) {
        return false
    }
    return editor.can().splitCell()
}

export function toggleSelectedTableHeaderRow(editor: Editor): boolean {
    return runAndRestoreTableSelection(editor, () => editor.commands.toggleHeaderRow())
}

export function toggleSelectedTableHeaderColumn(editor: Editor): boolean {
    return runAndRestoreTableSelection(editor, () => editor.commands.toggleHeaderColumn())
}

export function runTableAction(editor: Editor, action: TableActionType): boolean {
    switch (action) {
        case 'insertRowBefore':
            return insertTableRow(editor, 'before')
        case 'insertRowAfter':
            return insertTableRow(editor, 'after')
        case 'insertColumnBefore':
            return insertTableColumn(editor, 'before')
        case 'insertColumnAfter':
            return insertTableColumn(editor, 'after')
        case 'deleteRow':
            return deleteSelectedTableRow(editor)
        case 'deleteColumn':
            return deleteSelectedTableColumn(editor)
        case 'deleteTable':
            return deleteCurrentTable(editor)
        case 'mergeCells':
            return mergeSelectedTableCells(editor)
        case 'splitCell':
            return splitSelectedTableCell(editor)
        case 'toggleHeaderRow':
            return toggleSelectedTableHeaderRow(editor)
        case 'toggleHeaderColumn':
            return toggleSelectedTableHeaderColumn(editor)
        default:
            return false
    }
}

export function getTableAnchorRects(tableElement: HTMLTableElement): TableAnchorRects {
    const tableRect = tableElement.getBoundingClientRect()
    const rowRects = [...tableElement.rows].map(row => row.getBoundingClientRect())
    const columnRects = getColumnRects(tableElement)

    return {
        tableRect,
        rowRects,
        columnRects
    }
}

export function getActiveTableElement(editor: Editor): HTMLTableElement | null {
    const dom = editor.view.dom
    const selection = window.getSelection()
    const node = selection?.anchorNode

    if (node instanceof HTMLElement && node.closest('table.rich-text-table')) {
        return node.closest('table.rich-text-table')
    }

    if (node?.parentElement?.closest('table.rich-text-table')) {
        return node.parentElement.closest('table.rich-text-table')
    }

    const currentCell = getCurrentCellRect(editor)
    if (!currentCell) {
        return null
    }

    const cellElements = dom.querySelectorAll<HTMLElement>('td.rich-text-table-cell, th.rich-text-table-header')
    const targetCellElement = [...cellElements].find(el => {
        const pos = editor.view.posAtDOM(el, 0)
        return pos === currentCell.tableStart + currentCell.cellPos + 1
    })

    return targetCellElement?.closest('table.rich-text-table') ?? null
}

export function getCurrentTableDomInfo(editor: Editor) {
    const tableElement = getActiveTableElement(editor)
    const cellRect = getCurrentCellRect(editor)

    if (!tableElement || !cellRect) {
        return null
    }

    return {
        tableElement,
        selection: getTableSelectionInfo(editor),
        cellRect,
        anchorRects: getTableAnchorRects(tableElement)
    }
}

function getSelectedCellContentRanges(selection: TableSelectionInfo): Range[] {
    const ranges: Range[] = []
    const seen = new Set<number>()

    for (let row = selection.top; row < selection.bottom; row++) {
        for (let col = selection.left; col < selection.right; col++) {
            const cellPos = selection.map.map[row * selection.map.width + col]
            if (seen.has(cellPos)) {
                continue
            }

            seen.add(cellPos)

            const cell = selection.table.nodeAt(cellPos)
            if (!cell) {
                continue
            }

            const from = selection.tableStart + cellPos + 1
            const to = from + cell.content.size
            ranges.push({ from, to })
        }
    }

    return ranges
}

function getSelectedTableCells(selection: TableSelectionInfo) {
    const cells: ProsemirrorNode[] = []
    const seen = new Set<number>()

    for (let row = selection.top; row < selection.bottom; row++) {
        for (let col = selection.left; col < selection.right; col++) {
            const cellPos = selection.map.map[row * selection.map.width + col]
            if (seen.has(cellPos)) {
                continue
            }

            const cell = selection.table.nodeAt(cellPos)
            if (!cell) {
                continue
            }

            seen.add(cellPos)
            cells.push(cell)
        }
    }

    return cells
}

export function getTableSelectionBookmarkFromInfo(selection: TableSelectionInfo): TableSelectionBookmark {
    return {
        anchorCell: selection.tableStart + selection.map.positionAt(selection.top, selection.left, selection.table),
        headCell: selection.tableStart + selection.map.positionAt(selection.bottom - 1, selection.right - 1, selection.table)
    }
}

export function getCurrentTableSelectionBookmark(editor: Editor): TableSelectionBookmark | null {
    const selection = editor.state.selection
    if (!(selection instanceof CellSelection)) {
        return null
    }

    return {
        anchorCell: selection.$anchorCell.pos,
        headCell: selection.$headCell.pos
    }
}

export function restoreTableSelectionBookmark(editor: Editor, bookmark: TableSelectionBookmark) {
    return setTableSelection(editor, bookmark.anchorCell, bookmark.headCell)
}

function isMarkActiveInCell(
    cell: ProsemirrorNode,
    type: Extract<TableTextStyleAction, { type: 'bold' | 'italic' | 'underline' | 'strike' }>['type']
) {
    const markName = getMarkName(type)
    let hasText = false
    let isActive = true

    cell.descendants(node => {
        if (!node.isText) {
            return isActive
        }

        hasText = true
        if (!node.marks.some(mark => mark.type.name === markName)) {
            isActive = false
        }

        return isActive
    })

    return hasText ? isActive : false
}

function isTextAlignActiveInCell(cell: ProsemirrorNode, value: 'left' | 'center' | 'right') {
    let hasTextBlock = false
    let isActive = true

    cell.descendants(node => {
        if (!node.isTextblock) {
            return isActive
        }

        hasTextBlock = true
        if (node.attrs.textAlign !== value) {
            isActive = false
        }

        return isActive
    })

    return hasTextBlock ? isActive : false
}

function getMarkName(type: Extract<TableTextStyleAction, { type: 'bold' | 'italic' | 'underline' | 'strike' }>['type']) {
    switch (type) {
        case 'bold':
            return 'bold'
        case 'italic':
            return 'italic'
        case 'underline':
            return 'underline'
        case 'strike':
            return 'strike'
        default:
            return type
    }
}

function getRangeIndexes(start: number, end: number) {
    return Array.from({ length: end - start }, (_, index) => start + index)
}

function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max)
}

function collapseTableSelectionToCell(editor: Editor) {
    const currentCell = getCurrentCellRect(editor)
    if (!currentCell) {
        return true
    }

    return editor
        .chain()
        .focus()
        .setTextSelection(currentCell.tableStart + currentCell.cellPos + 1)
        .run()
}

function insertAndFocusTableCell(editor: Editor, axis: TableSelectionAxis, direction: TableInsertDirection, targetIndex?: number) {
    const beforeInsertCell = getCurrentCellRect(editor)
    if (!beforeInsertCell) {
        return false
    }

    const inserted =
        axis === 'row'
            ? direction === 'before'
                ? editor.commands.addRowBefore()
                : editor.commands.addRowAfter()
            : direction === 'before'
            ? editor.commands.addColumnBefore()
            : editor.commands.addColumnAfter()

    if (!inserted) {
        return false
    }

    const afterInsertCell = getCurrentCellRect(editor)
    if (!afterInsertCell) {
        return collapseTableSelectionToCell(editor)
    }

    const targetRow =
        axis === 'row'
            ? clamp((targetIndex ?? beforeInsertCell.row) + (direction === 'after' ? 1 : 0), 0, afterInsertCell.map.height - 1)
            : clamp(beforeInsertCell.row, 0, afterInsertCell.map.height - 1)
    const targetCol =
        axis === 'column'
            ? clamp((targetIndex ?? beforeInsertCell.col) + (direction === 'after' ? 1 : 0), 0, afterInsertCell.map.width - 1)
            : clamp(beforeInsertCell.col, 0, afterInsertCell.map.width - 1)

    return focusTableCellAt(editor, afterInsertCell, targetRow, targetCol)
}

function focusTableCellAt(editor: Editor, currentCell: TableCellRect, row: number, col: number) {
    const cellPos = currentCell.map.positionAt(row, col, currentCell.table)
    return editor
        .chain()
        .focus()
        .setTextSelection(currentCell.tableStart + cellPos + 1)
        .run()
}

function runAndRestoreTableSelection(editor: Editor, action: () => boolean) {
    const snapshot = captureTableSelectionSnapshot(editor)
    const done = action()
    if (!done) {
        return false
    }

    return snapshot ? restoreTableSelectionFromSnapshot(editor, snapshot) : true
}

function captureTableSelectionSnapshot(editor: Editor): TableSelectionSnapshot | null {
    const selection = getTableSelectionInfo(editor)
    const currentCell = getCurrentCellRect(editor)
    if (!selection || !currentCell) {
        return null
    }

    return {
        variant: getTableSelectionVariantFromInfo(selection),
        top: selection.top,
        bottom: selection.bottom,
        left: selection.left,
        right: selection.right,
        currentRow: currentCell.row,
        currentCol: currentCell.col
    }
}

function restoreTableSelectionFromSnapshot(editor: Editor, snapshot: TableSelectionSnapshot) {
    const currentCell = getCurrentCellRect(editor)
    if (!currentCell) {
        return true
    }

    const top = clamp(snapshot.top, 0, currentCell.map.height - 1)
    const bottom = clamp(snapshot.bottom - 1, 0, currentCell.map.height - 1)
    const left = clamp(snapshot.left, 0, currentCell.map.width - 1)
    const right = clamp(snapshot.right - 1, 0, currentCell.map.width - 1)

    switch (snapshot.variant) {
        case 'row': {
            return restoreTableSelectionBookmark(editor, {
                anchorCell: currentCell.tableStart + currentCell.map.positionAt(top, 0, currentCell.table),
                headCell: currentCell.tableStart + currentCell.map.positionAt(bottom, currentCell.map.width - 1, currentCell.table)
            })
        }
        case 'column': {
            return restoreTableSelectionBookmark(editor, {
                anchorCell: currentCell.tableStart + currentCell.map.positionAt(0, left, currentCell.table),
                headCell: currentCell.tableStart + currentCell.map.positionAt(currentCell.map.height - 1, right, currentCell.table)
            })
        }
        case 'cellRange': {
            return restoreTableSelectionBookmark(editor, {
                anchorCell: currentCell.tableStart + currentCell.map.positionAt(top, left, currentCell.table),
                headCell: currentCell.tableStart + currentCell.map.positionAt(bottom, right, currentCell.table)
            })
        }
        case 'cell':
        default:
            return focusTableCellAt(
                editor,
                currentCell,
                clamp(snapshot.currentRow, 0, currentCell.map.height - 1),
                clamp(snapshot.currentCol, 0, currentCell.map.width - 1)
            )
    }
}

function getColumnRects(tableElement: HTMLTableElement): DOMRect[] {
    const firstRow = tableElement.rows.item(0)
    if (!firstRow) {
        return []
    }

    const cells = [...firstRow.cells]
    const rects: DOMRect[] = []

    cells.forEach(cell => {
        const colspan = Number(cell.getAttribute('colspan') || 1)
        const cellRect = cell.getBoundingClientRect()
        const sliceWidth = cellRect.width / colspan

        for (let index = 0; index < colspan; index++) {
            rects.push(createColumnRect(cellRect, index, sliceWidth))
        }
    })

    return rects
}

function createColumnRect(cellRect: DOMRect, index: number, sliceWidth: number): DOMRect {
    return {
        x: cellRect.x + sliceWidth * index,
        y: cellRect.y,
        width: sliceWidth,
        height: cellRect.height,
        top: cellRect.top,
        right: cellRect.x + sliceWidth * (index + 1),
        bottom: cellRect.bottom,
        left: cellRect.x + sliceWidth * index,
        toJSON: cellRect.toJSON.bind(cellRect)
    }
}

export function getTableCellNodes(doc: ProsemirrorNode) {
    return findChildren(doc, node => TABLE_CELL_NODE_NAMES.has(node.type.name))
}

export function hasTableNode(doc: ProsemirrorNode) {
    return findChildren(doc, node => node.type.name === 'table').length > 0
}

export function isTableRowNode(node: ProsemirrorNode) {
    return node.type.name === TABLE_ROW_NODE_NAME
}

export function getTableInfoFromPosition(editor: Editor, pos: number) {
    const $pos = editor.state.doc.resolve(pos)
    return findTable($pos)
}

export function getSelectedTableCellElements(editor: Editor, selection: TableSelectionInfo, tableElement?: HTMLTableElement | null) {
    const activeTableElement = tableElement ?? getActiveTableElement(editor)
    if (!activeTableElement) {
        return []
    }

    return getSelectedTableCellPositions(selection)
        .map(cellPos => getTableCellElementAtPosition(editor, selection.tableStart + cellPos, activeTableElement))
        .filter((cell): cell is HTMLTableCellElement => Boolean(cell))
}

export function getSelectedTableCellBounds(editor: Editor, selection: TableSelectionInfo, tableElement?: HTMLTableElement | null) {
    const cells = getSelectedTableCellElements(editor, selection, tableElement)
    const firstCell = cells[0]

    if (!firstCell) {
        return null
    }

    const initialRect = firstCell.getBoundingClientRect()
    const bounds = {
        top: initialRect.top,
        left: initialRect.left,
        right: initialRect.right,
        bottom: initialRect.bottom
    }

    cells.slice(1).forEach(cell => {
        const rect = cell.getBoundingClientRect()
        bounds.top = Math.min(bounds.top, rect.top)
        bounds.left = Math.min(bounds.left, rect.left)
        bounds.right = Math.max(bounds.right, rect.right)
        bounds.bottom = Math.max(bounds.bottom, rect.bottom)
    })

    return {
        top: bounds.top,
        left: bounds.left,
        width: bounds.right - bounds.left,
        height: bounds.bottom - bounds.top
    }
}

export function getSelectedTableCellCount(selection: TableSelectionInfo) {
    return getSelectedTableCellPositions(selection).length
}

export function buildSelectionOutlineSegments(
    rects: Array<Pick<DOMRect, 'top' | 'left' | 'right' | 'bottom'>>,
    thickness = 2
): TableSelectionOutlineSegment[] {
    const edgeMap = new Map<string, OutlineEdge>()

    rects.forEach(rect => {
        addOutlineEdge(edgeMap, createHorizontalEdge(rect.top, rect.left, rect.right))
        addOutlineEdge(edgeMap, createHorizontalEdge(rect.bottom, rect.left, rect.right))
        addOutlineEdge(edgeMap, createVerticalEdge(rect.left, rect.top, rect.bottom))
        addOutlineEdge(edgeMap, createVerticalEdge(rect.right, rect.top, rect.bottom))
    })

    const horizontalEdges = mergeOutlineEdges([...edgeMap.values()].filter(edge => edge.axis === 'horizontal').sort(compareOutlineEdges))
    const verticalEdges = mergeOutlineEdges([...edgeMap.values()].filter(edge => edge.axis === 'vertical').sort(compareOutlineEdges))

    return [...horizontalEdges, ...verticalEdges].map(edge => outlineEdgeToSegment(edge, thickness))
}

function getSelectedTableCellPositions(selection: TableSelectionInfo) {
    const cells = new Set<number>()

    for (let row = selection.top; row < selection.bottom; row++) {
        for (let col = selection.left; col < selection.right; col++) {
            cells.add(selection.map.map[row * selection.map.width + col])
        }
    }

    return [...cells]
}

function getTableCellElementAtPosition(editor: Editor, pos: number, tableElement: HTMLTableElement): HTMLTableCellElement | null {
    const positions = [pos, pos + 1]

    for (const currentPos of positions) {
        const node = editor.view.nodeDOM(currentPos)

        if (node instanceof HTMLTableCellElement) {
            return tableElement.contains(node) ? node : null
        }

        if (node instanceof HTMLElement) {
            const cell = node.closest('td.rich-text-table-cell, th.rich-text-table-header')
            if (cell instanceof HTMLTableCellElement && tableElement.contains(cell)) {
                return cell
            }
        }
    }

    return null
}

type OutlineEdge =
    | {
          axis: 'horizontal'
          line: number
          start: number
          end: number
      }
    | {
          axis: 'vertical'
          line: number
          start: number
          end: number
      }

function createHorizontalEdge(line: number, start: number, end: number): OutlineEdge {
    return {
        axis: 'horizontal',
        line: roundOutlineCoord(line),
        start: roundOutlineCoord(Math.min(start, end)),
        end: roundOutlineCoord(Math.max(start, end))
    }
}

function createVerticalEdge(line: number, start: number, end: number): OutlineEdge {
    return {
        axis: 'vertical',
        line: roundOutlineCoord(line),
        start: roundOutlineCoord(Math.min(start, end)),
        end: roundOutlineCoord(Math.max(start, end))
    }
}

function roundOutlineCoord(value: number) {
    return Math.round(value * 2) / 2
}

function getOutlineEdgeKey(edge: OutlineEdge) {
    return `${edge.axis}:${edge.line}:${edge.start}:${edge.end}`
}

function addOutlineEdge(map: Map<string, OutlineEdge>, edge: OutlineEdge) {
    const key = getOutlineEdgeKey(edge)

    if (map.has(key)) {
        map.delete(key)
        return
    }

    map.set(key, edge)
}

function compareOutlineEdges(a: OutlineEdge, b: OutlineEdge) {
    if (a.axis !== b.axis) {
        return a.axis.localeCompare(b.axis)
    }
    if (a.line !== b.line) {
        return a.line - b.line
    }
    if (a.start !== b.start) {
        return a.start - b.start
    }
    return a.end - b.end
}

function mergeOutlineEdges(edges: OutlineEdge[]) {
    if (edges.length === 0) {
        return []
    }

    const merged: OutlineEdge[] = []

    edges.forEach(edge => {
        const previous = merged[merged.length - 1]
        if (previous && previous.axis === edge.axis && previous.line === edge.line && Math.abs(previous.end - edge.start) <= 0.5) {
            previous.end = Math.max(previous.end, edge.end)
            return
        }

        merged.push({ ...edge })
    })

    return merged
}

function outlineEdgeToSegment(edge: OutlineEdge, thickness: number): TableSelectionOutlineSegment {
    if (edge.axis === 'horizontal') {
        return {
            top: edge.line - thickness / 2,
            left: edge.start,
            width: Math.max(edge.end - edge.start, thickness),
            height: thickness
        }
    }

    return {
        top: edge.start,
        left: edge.line - thickness / 2,
        width: thickness,
        height: Math.max(edge.end - edge.start, thickness)
    }
}

export function isTableCellElementInSelection(editor: Editor, cellElement: HTMLElement) {
    const selection = editor.state.selection

    try {
        const pos = editor.view.posAtDOM(cellElement, 0)
        return pos >= selection.from && pos <= selection.to
    } catch {
        return false
    }
}

export function focusTableCellFromElement(editor: Editor, cellElement: HTMLElement) {
    try {
        const pos = editor.view.posAtDOM(cellElement, 0)
        return editor
            .chain()
            .focus()
            .setTextSelection(pos + 1)
            .run()
    } catch {
        return false
    }
}
